/**
 * Staff ops attribution page.
 *
 * Sprint 1 surface — manual reconciliation of inbound wires that
 * arrived without a usable reference number. Two stacked sections:
 *
 *   1. Record incoming wire — POST /api/v1/ops/bank-transactions
 *   2. Unmatched queue       — GET  /api/v1/ops/bank-transactions/unmatched
 *      (credits only, #311: debits live in Reconciliation → Debits). A credit the
 *      matching rules left here says why and what can be done with it.
 *      Each row has an Attribute action that pops a panel for client
 *      lookup (by sender CLABE) + product selection. Submitting calls
 *      POST /api/v1/ops/bank-transactions/{id}/attribute, which
 *      creates a pending deposit_request and marks the row matched
 *      atomically. A *different* manager then approves the resulting
 *      pending deposit via the existing /manager/deposits queue —
 *      SOD invariant preserved (see schema CHECK
 *      chk_deposit_sod_proposer_not_reviewer).
 *
 * Roles: agent · manager · super_admin (router-level RBAC on
 * /ops/bank-transactions + /ops/client-bank-accounts/sender-lookup).
 */
import { formatCents, limitToCents } from '../../lib/amount';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import {
  RETURN_REASONS,
  type ReturnDraft,
  canReturn,
  describeReturnOutcome,
  feeExceedsAmount,
  isReturnDraftComplete,
  netReturned,
} from './bankReturns.helpers';
import bankTransactionsService, {
  type BankTransaction,
  type ReturnReason,
  type ConcentrationAccountOption,
} from '../../services/bankTransactions.service';
import bankAccountsService, {
  type ClientBankAccount,
  type ClientBankAccountFull,
} from '../../services/bankAccounts.service';
import depositsService from '../../services/deposits.service';
import portfolioService from '../../services/portfolio.service';
import type { DepositRequest } from '../../types/finance';
import authService from '../../services/auth.service';
import agentService from '../../services/agent.service';
import managerService, { type ClientSummary } from '../../services/manager.service';
import { fullName } from '../../lib/fullName';
import { getApiErrorMessage } from '../../lib/apiError';
import VoidLinePanel from './VoidLinePanel';
import { queueRefusal } from './attributionQueue.helpers';
import VoidRequestPanel from './VoidRequestPanel';
import { cleanMfaInput, isMfaCode, stepUpErrorMessage } from '../../lib/mfaApproval';
import { colors } from '../../styles/theme';
import { readerLocale } from '../../i18n/language';
import { readerCountryName } from '../../lib/countries';


interface SimpleProduct {
  id: string;
  name: string;
  /** The vehicle's currency, read live from funds (0073). Null when funds could
   *  not answer — the product is still listed, without its currency. */
  currency_code: string | null;
}

export default function AttributeDeposit(): JSX.Element {
  const [queue, setQueue] = useState<BankTransaction[]>([]);
  const [products, setProducts] = useState<SimpleProduct[]>([]);
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [activeTxId, setActiveTxId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The client roster powers the "Client" picker on each attribution
      // panel — scoped by role (an agent sees their own clients; a manager /
      // super_admin sees all). Sender-lookup matches are merged in per panel.
      const role = authService.getRole();
      const [rows, prods, cls] = await Promise.all([
        bankTransactionsService.listUnmatched(),
        // Operational product listing (all active products) — NOT the
        // pre-approval marketing catalog, which caps limit at 10. Ops must be
        // able to attribute a wire to ANY active product.
        portfolioService.listProducts(),
        role === 'agent' ? agentService.listMyClients() : managerService.listClients(),
      ]);
      setQueue(rows);
      setProducts(prods.map((p) => ({
        id: p.id, name: p.name, currency_code: p.currency_code,
      })));
      setClients(cls);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load the attribution queue.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Sending money back is a manager's decision (the server refuses an agent);
  // an agent still sees the queue and what happened to each row.
  const canReturnMoney = authService.getRole() !== 'agent';
  // Operations attribute money (owner, 2026-10-31): a manager proposes, a different manager
  // approves. An agent sees the queue and can ask for a void, never attribute.
  const canAttribute = authService.getRole() !== 'agent';

  const handleReturned = useCallback((message: string) => {
    setFlash(message);
    globalThis.setTimeout(() => setFlash(null), 8000);
    reload();
  }, [reload]);

  const handleAttributed = useCallback((depositRequestId: string) => {
    setFlash(
      `Created pending deposit ${depositRequestId.slice(0, 8)}…. ` +
      'A manager must now approve it via the manager deposits queue.',
    );
    setActiveTxId(null);
    globalThis.setTimeout(() => setFlash(null), 8000);
    reload();
  }, [reload]);

  return (
    <>
      <PageHeader
        title="Attribute deposit"
        subtitle="Record inbound wires and link them to a client + product."
      />

      {flash && (
        <div className="rounded-lg p-3 mb-4 text-sm"
             style={{ background: colors.successBg, color: colors.success,
                      border: `1px solid ${colors.successBorder}` }}>
          {flash}
        </div>
      )}

      {error && (
        <div className="rounded-lg p-3 mb-4 text-sm" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger,
                      border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}

      {!canAttribute && (
        <div className="rounded-lg p-3 mb-4 text-sm" role="note"
             style={{ background: colors.infoBg, color: colors.infoText,
                      border: `1px solid ${colors.infoBorder}` }}>
          Operations attribute transfers: a manager proposes it and a different manager approves it.
          If a client told you about a transfer, record their transfer notice from their page.
        </div>
      )}

      <RecordManualEntry onRecorded={() => { reload(); }} />

      <section className="mt-6">
        <h2 className="text-sm font-bold uppercase tracking-widest mb-3"
            style={{ color: colors.inkSoft }}>
          Unmatched queue
        </h2>

        {loading && (
          <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>
        )}

        {!loading && queue.length === 0 && (
          <div className="rounded-2xl p-6 text-sm"
               style={{ background: colors.surface,
                        border: `1px solid ${colors.border}`,
                        color: colors.muted }}>
            No unmatched bank transactions. Inbound wires recorded above
            will appear here until they&apos;re attributed.
          </div>
        )}

        {!loading && queue.length > 0 && (
          <ul className="space-y-3">
            {queue.map((tx) => (
              <li key={tx.id}>
                <UnmatchedTxCard
                  tx={tx}
                  products={products}
                  clients={clients}
                  expanded={activeTxId === tx.id}
                  onToggle={() =>
                    setActiveTxId(activeTxId === tx.id ? null : tx.id)
                  }
                  onAttributed={handleAttributed}
                  canAttribute={canAttribute}
                  canReturnMoney={canReturnMoney}
                  onReturned={handleReturned}
                />
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}


// ── Manual entry form ─────────────────────────────────────────────


interface RecordManualEntryProps {
  onRecorded: () => void;
}

function RecordManualEntry({ onRecorded }: Readonly<RecordManualEntryProps>): JSX.Element {
  const [open, setOpen] = useState(false);
  const [concentrationAccountId, setConcentrationAccountId] = useState('');
  const [accounts, setAccounts] = useState<ConcentrationAccountOption[]>([]);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('MXN');
  const [postedAt, setPostedAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [senderName, setSenderName] = useState('');
  const [senderAccount, setSenderAccount] = useState('');
  const [referenceText, setReferenceText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load active concentration accounts once the form is opened. Operators
  // pick a destination from this list instead of pasting a raw UUID.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await bankTransactionsService.listConcentrationAccounts();
        if (!cancelled) setAccounts(rows);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(getApiErrorMessage(e, 'Could not load concentration accounts.'));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Only accounts in the selected currency are valid destinations for the
  // wire. Drop a stale selection when the currency changes underneath it.
  const matchingAccounts = accounts.filter((a) => a.currency === currency);
  useEffect(() => {
    if (concentrationAccountId &&
        !matchingAccounts.some((a) => a.id === concentrationAccountId)) {
      setConcentrationAccountId('');
    }
  }, [currency, accounts]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await bankTransactionsService.record({
        concentration_account_id: concentrationAccountId.trim(),
        posted_at: new Date(postedAt).toISOString(),
        amount: amount.trim(),
        currency_code: currency.trim().toUpperCase(),
        sender_name: senderName.trim() || null,
        sender_account: senderAccount.trim() || null,
        reference_text: referenceText.trim() || null,
      });
      setAmount('');
      setSenderName('');
      setSenderAccount('');
      setReferenceText('');
      setOpen(false);
      onRecorded();
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not record the entry.'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-sm px-3 py-2 rounded-lg font-semibold"
        style={{ background: colors.gold, color: colors.navy }}
      >
        + Record incoming wire
      </button>
    );
  }

  return (
    <div className="rounded-2xl p-5"
         style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <h2 className="text-sm font-bold uppercase tracking-widest mb-3"
          style={{ color: colors.inkSoft }}>
        Record incoming wire
      </h2>

      {error && (
        <div className="rounded-lg p-2 mb-3 text-xs" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger,
                      border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Concentration account">
          <select value={concentrationAccountId}
                  onChange={(e) => setConcentrationAccountId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={inputStyle}>
            <option value="">— select —</option>
            {matchingAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.beneficiary} · {a.market} ({a.transfer_method})
              </option>
            ))}
          </select>
          {matchingAccounts.length === 0 && (
            <span className="block text-xs mt-1" style={{ color: colors.muted }}>
              No active {currency} account configured.
            </span>
          )}
        </Field>
        <Field label="Posted at (UTC)">
          <input type="datetime-local" value={postedAt}
                 onChange={(e) => setPostedAt(e.target.value)}
                 className="w-full px-3 py-2 rounded-lg text-sm"
                 style={inputStyle} />
        </Field>
        <Field label="Amount">
          <input type="text" inputMode="decimal" value={amount}
                 onChange={(e) => setAmount(limitToCents(e.target.value))}
                 className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                 placeholder="0.00" style={inputStyle} />
        </Field>
        <Field label="Currency">
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
            <option value="MXN">MXN</option>
            <option value="USD">USD</option>
            <option value="CAD">CAD</option>
          </select>
        </Field>
        <Field label="Sender name (optional)">
          <input value={senderName}
                 onChange={(e) => setSenderName(e.target.value)}
                 className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
        </Field>
        <Field label="Sender account (optional)">
          <input value={senderAccount}
                 onChange={(e) => setSenderAccount(e.target.value)}
                 placeholder="CLABE / ABA / CPA"
                 className="w-full px-3 py-2 rounded-lg text-sm font-mono" style={inputStyle} />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Reference text (concepto/memo, optional)">
            <input value={referenceText}
                   onChange={(e) => setReferenceText(e.target.value)}
                   className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </Field>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button type="button" onClick={() => setOpen(false)}
                className="text-sm px-3 py-1.5 rounded-lg"
                style={{ background: 'transparent', color: colors.inkSoft,
                         border: `1px solid ${colors.border}` }}>
          Cancel
        </button>
        <button type="button" onClick={() => { submit(); }}
                disabled={saving || !amount || !concentrationAccountId}
                className="text-sm px-3 py-1.5 rounded-lg font-semibold"
                style={{ background: colors.gold, color: colors.navy }}>
          {saving ? 'Recording…' : 'Record wire'}
        </button>
      </div>
    </div>
  );
}


// ── Per-row attribution card ─────────────────────────────────────


interface UnmatchedTxCardProps {
  tx: BankTransaction;
  products: SimpleProduct[];
  clients: ClientSummary[];
  expanded: boolean;
  onToggle: () => void;
  onAttributed: (depositRequestId: string) => void;
  /** Attribution is operations' (owner, 2026-10-31); an agent sees the queue, not the button. */
  canAttribute: boolean;
  /** A return is a manager's decision; an agent sees the queue, not the button. */
  canReturnMoney: boolean;
  onReturned: (message: string) => void;
}

function UnmatchedTxCard({
  tx, products, clients, expanded, onToggle, onAttributed, canAttribute, canReturnMoney, onReturned,
}: Readonly<UnmatchedTxCardProps>): JSX.Element {
  const posted = new Date(tx.posted_at).toLocaleString(readerLocale());
  const refused = queueRefusal(tx.auto_match_refusal);
  const [returning, setReturning] = useState(false);
  const [voiding, setVoiding] = useState(false);
  return (
    <div className="rounded-2xl p-4"
         style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold" style={{ color: colors.ink }}>
            {tx.currency_code} {formatCents(tx.amount)}
          </div>
          <div className="text-xs" style={{ color: colors.muted }}>
            {posted} · {tx.sender_name || '—'} {tx.sender_account ? `· ····${tx.sender_account.slice(-4)}` : ''}
          </div>
          {tx.reference_text && (
            <div className="text-xs mt-0.5" style={{ color: colors.inkSoft }}>
              &ldquo;{tx.reference_text}&rdquo;
            </div>
          )}
          {refused && !tx.blocked && (
            <div className="text-xs mt-1" style={{ color: colors.inkSoft }}
                 data-testid="queue-refusal">
              <span className="font-semibold">Not matched automatically:</span>{' '}
              <span>{refused.reason}</span>{' '}
              <span>{refused.action}</span>
            </div>
          )}
        </div>
        {tx.blocked ? (
          <p className="text-xs max-w-xs text-right" style={{ color: colors.muted }}>
            This money is on hold pending a compliance review. It stays where it is until compliance decides, so
            it cannot be sent back, voided or attributed.
          </p>
        ) : (
        <div className="flex gap-2 flex-shrink-0">
          {canReturnMoney && canReturn({ status: tx.status, direction: 'credit' }) && (
            <button type="button" onClick={() => setReturning((v) => !v)}
                    className="text-xs px-3 py-1.5 rounded-md font-semibold"
                    style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
              {returning ? 'Cancel' : 'Send back'}
            </button>
          )}
          {tx.status === 'unmatched' && tx.void_request === null && (
            <button type="button" onClick={() => setVoiding((v) => !v)}
                    className="text-xs px-3 py-1.5 rounded-md font-semibold"
                    style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
              {voiding ? 'Cancel void' : 'Void line'}
            </button>
          )}
          {canAttribute && (
            <button type="button" onClick={onToggle}
                    className="text-xs px-3 py-1.5 rounded-md font-semibold"
                    style={{ background: colors.gold, color: colors.navy }}>
              {expanded ? 'Close' : 'Attribute'}
            </button>
          )}
        </div>
        )}
      </div>

      {expanded && canAttribute && !tx.blocked && (
        <AttributionPanel
          tx={tx}
          products={products}
          clients={clients}
          onAttributed={onAttributed}
        />
      )}

      {tx.void_request !== null && (
        <VoidRequestPanel tx={tx} request={tx.void_request} onDone={onReturned} />
      )}

      {voiding && tx.void_request === null && (
        <VoidLinePanel
          tx={tx}
          onVoided={(msg) => { setVoiding(false); onReturned(msg); }}
        />
      )}

      {returning && (
        <ReturnPanel
          tx={tx}
          onDone={(msg) => { setReturning(false); onReturned(msg); }}
        />
      )}
    </div>
  );
}

/**
 * Send money back where it came from, and record that it happened (0086).
 *
 * The person looking at a wire they cannot attribute is the person who decides
 * it goes back, so the action lives here rather than on a queue of its own.
 */
/**
 * What the sender ends up with, in one sentence.
 *
 * Separate from the component because a fee larger than the payment is not a
 * smaller version of the normal case — it is a number someone typed wrong, and
 * saying so is the only useful thing to print.
 */
function describeNet(feeExceedsAmount: boolean, net: number | null, currency: string): string {
  if (feeExceedsAmount) return 'The charge is more than the money. Check the number.';
  const amount = net === null ? '—' : net.toFixed(2);
  return `The charge comes out of the amount, so the sender receives ${currency} ${amount}. `
    + 'They are told that.';
}

function ReturnPanel({ tx, onDone }: Readonly<{
  tx: BankTransaction;
  onDone: (message: string) => void;
}>): JSX.Element {
  const [draft, setDraft] = useState<ReturnDraft>({ reason: '', instruction: '', fee: '' });
  const [mfaCode, setMfaCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const net = netReturned(tx.amount, draft.fee);
  const tooMuch = feeExceedsAmount(tx.amount, draft.fee);
  const ready = isReturnDraftComplete(draft) && !tooMuch && isMfaCode(mfaCode) && !busy;

  const send = async (): Promise<void> => {
    if (draft.reason === '') return;
    setBusy(true);
    setError(null);
    try {
      const outcome = await bankTransactionsService.returnTransfer(tx.id, {
        reason: draft.reason,
        instruction: draft.instruction.trim(),
        fee: draft.fee.trim() === '' ? '0' : draft.fee.trim(),
        mfa_code: mfaCode,
      });
      onDone(describeReturnOutcome(outcome));
    } catch (e: unknown) {
      setError(stepUpErrorMessage(e, 'Could not send the money back.'));
    } finally {
      setBusy(false);
      setMfaCode('');
    }
  };

  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
      {error && (
        <div className="rounded-lg p-2 mb-3 text-xs" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger,
                      border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Why can we not keep it?">
          <select value={draft.reason}
                  onChange={(e) => setDraft({ ...draft, reason: e.target.value as ReturnReason })}
                  className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
            <option value="">— select —</option>
            {RETURN_REASONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </Field>
        <Field label={`Bank's charge for the return (${tx.currency_code})`}>
          <input value={draft.fee}
                 onChange={(e) => setDraft({ ...draft, fee: limitToCents(e.target.value) })}
                 inputMode="decimal" placeholder="0.00"
                 className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
        </Field>
        <div className="sm:col-span-2">
          <Field label="What did you instruct the bank to do?">
            <textarea value={draft.instruction}
                      onChange={(e) => setDraft({ ...draft, instruction: e.target.value })}
                      rows={2}
                      placeholder="Returned to the originating account by wire, same day."
                      className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </Field>
        </div>
      </div>

      <p className="text-xs mt-2" style={{ color: tooMuch ? colors.danger : colors.mutedSoft }}>
        {describeNet(tooMuch, net, tx.currency_code)}
      </p>

      <div className="mt-3">
        <Field label="Authenticator code">
          <input value={mfaCode}
                 onChange={(e) => setMfaCode(cleanMfaInput(e.target.value))}
                 inputMode="numeric" autoComplete="one-time-code"
                 className="w-40 px-3 py-2 rounded-lg text-sm" style={inputStyle} />
        </Field>
        {!isMfaCode(mfaCode) && (
          <p className="text-xs mt-1" style={{ color: colors.warning }}>
            Sending money back needs your 6-digit authenticator code.
          </p>
        )}
      </div>

      <div className="flex justify-end mt-3">
        <button type="button" onClick={() => { void send(); }} disabled={!ready}
                className="text-sm px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50"
                style={{ background: colors.navy, color: '#FFFFFF' }}>
          {busy ? 'Sending back…' : 'Send it back'}
        </button>
      </div>
    </div>
  );
}


interface AttributionPanelProps {
  tx: BankTransaction;
  products: SimpleProduct[];
  clients: ClientSummary[];
  onAttributed: (depositRequestId: string) => void;
}

function AttributionPanel({
  tx, products, clients, onAttributed,
}: Readonly<AttributionPanelProps>): JSX.Element {
  const [lookupMatches, setLookupMatches] = useState<ClientBankAccountFull[]>([]);
  const [lookupRan, setLookupRan] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [userId, setUserId] = useState('');
  const [productId, setProductId] = useState('');
  // Agent-proposed deposits for the chosen client still "awaiting wire" whose
  // amount + currency equal this wire's. Picking one matches the wire to THAT
  // request (no duplicate deposit_request); leaving it blank creates a new one.
  const [awaiting, setAwaiting] = useState<DepositRequest[]>([]);
  // Set when the lookup itself failed, so an empty list is never read as
  // "this client has no pending request".
  const [awaitingError, setAwaitingError] = useState<string | null>(null);
  const [existingRequestId, setExistingRequestId] = useState('');
  const [matchReason, setMatchReason] = useState('');
  const [notes, setNotes] = useState('');
  // 0083: which registered account of the CHOSEN client this wire came from.
  // Required when the bank reported no originator — without it the server
  // refuses the attribution (`sender_account_missing`), because money is only
  // ever credited to the person who sent it.
  const [sourceAccountId, setSourceAccountId] = useState('');
  const [clientAccounts, setClientAccounts] = useState<ClientBankAccount[]>([]);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Options for the client picker: the role-scoped roster merged with any
  // sender-lookup match (so a suggested holder is always selectable even when
  // they're outside the current user's roster), deduped by user id.
  const clientOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const c of clients) byId.set(c.id, `${fullName(c)} · ${c.email}`);
    for (const m of lookupMatches) {
      if (!byId.has(m.user_id)) byId.set(m.user_id, `${m.holder_name} (sender match)`);
    }
    return [...byId.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [clients, lookupMatches]);

  const runLookup = useCallback(async () => {
    if (!tx.sender_account) {
      setLookupRan(true);
      return;
    }
    setLookupLoading(true);
    setError(null);
    try {
      const rows = await bankAccountsService.senderLookup(tx.sender_account);
      setLookupMatches(rows);
      setLookupRan(true);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Sender lookup failed.'));
    } finally {
      setLookupLoading(false);
    }
  }, [tx.sender_account]);

  useEffect(() => { runLookup(); }, [runLookup]);

  useEffect(() => {
    setExistingRequestId('');
    setAwaiting([]);
    setAwaitingError(null);
    if (!userId) return;
    let cancelled = false;
    depositsService
      .list({ status: ['pending'], user_id: userId, size: 100 })
      .then((res) => {
        if (cancelled) return;
        setAwaiting(res.items.filter((d) => isWireCandidate(d, tx)));
      })
      .catch((e: unknown) => {
        // The form still works without this list, but an empty list used to be
        // indistinguishable from "no pending request", which led staff to create
        // a duplicate deposit for a wire that already had one waiting.
        if (!cancelled) {
          setAwaiting([]);
          setAwaitingError(getApiErrorMessage(e, 'Could not check for existing pending deposits.'));
        }
      });
    return () => { cancelled = true; };
  }, [userId, tx]);

  const chooseExisting = (requestId: string): void => {
    setExistingRequestId(requestId);
    const chosen = awaiting.find((d) => d.id === requestId);
    if (chosen) setProductId(chosen.product_id);
  };

  // The picker only makes sense once a client is chosen, and it must be reloaded
  // when the choice changes: these are THEIR accounts, and naming someone else's
  // is exactly what the own-account rule exists to stop.
  useEffect(() => {
    setSourceAccountId('');
    setAccountsError(null);
    if (!userId) {
      setClientAccounts([]);
      return;
    }
    let cancelled = false;
    bankAccountsService
      .listForClient(userId)
      .then((rows) => { if (!cancelled) setClientAccounts(rows); })
      .catch((e: unknown) => {
        if (cancelled) return;
        setClientAccounts([]);
        setAccountsError(getApiErrorMessage(e, 'Could not read this client’s accounts.'));
      });
    return () => { cancelled = true; };
  }, [userId]);

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await bankTransactionsService.attribute(tx.id, {
        user_id: userId.trim(),
        product_id: productId,
        match_reason: matchReason.trim(),
        notes: notes.trim() || null,
        deposit_request_id: existingRequestId || null,
        source_client_bank_account_id: sourceAccountId || null,
      });
      onAttributed(res.deposit_request_id);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not attribute the wire.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
      {error && (
        <div className="rounded-lg p-2 mb-3 text-xs" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger,
                      border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}

      {lookupLoading && (
        <p className="text-xs" style={{ color: colors.muted }}>
          Looking up sender account…
        </p>
      )}

      {lookupRan && lookupMatches.length > 0 && (
        <div className="rounded-lg p-3 mb-3"
             style={{ background: colors.canvas,
                      border: `1px solid ${colors.border}` }}>
          <p className="text-xs mb-2" style={{ color: colors.muted }}>
            Matching registered accounts (auto-suggestion):
          </p>
          <ul className="space-y-2">
            {lookupMatches.map((m) => (
              <li key={m.id}>
                <button type="button"
                        onClick={() => setUserId(m.user_id)}
                        className="text-left text-sm px-3 py-2 rounded-md w-full"
                        style={{ background: colors.surface,
                                 border: `1px solid ${colors.border}`,
                                 color: colors.ink }}>
                  <div className="font-medium">{m.holder_name}</div>
                  <div className="text-xs font-mono" style={{ color: colors.muted }}>
                    {m.account_type} · {readerCountryName(m.country_code)} · {m.account_number}
                  </div>
                </button>
              </li>
            ))}
          </ul>
          <p className="text-xs mt-2" style={{ color: colors.mutedSoft }}>
            Click a row to copy the holder&apos;s user id into the form below.
          </p>
        </div>
      )}

      {lookupRan && lookupMatches.length === 0 && tx.sender_account && (
        <p className="text-xs mb-3" style={{ color: colors.muted }}>
          No registered client account matches this sender. Confirm the
          client manually before attributing.
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Client">
          <select value={userId} onChange={(e) => setUserId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
            <option value="">— select client —</option>
            {clientOptions.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </Field>
        <AwaitingLookupWarning message={awaitingError} />
        {awaiting.length > 0 && (
          <div className="sm:col-span-2">
            <Field label="Existing pending request awaiting this wire (optional)">
              <select value={existingRequestId}
                      onChange={(e) => chooseExisting(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
                <option value="">— create a new pending deposit —</option>
                {awaiting.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.reference_number ?? d.id.slice(0, 8)} · {d.product_name} · {d.amount} {d.currency_code}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}
        <Field label="Product">
          <select value={productId} onChange={(e) => setProductId(e.target.value)}
                  disabled={existingRequestId !== ''}
                  className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
            <option value="">— select —</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.currency_code ? `${p.name} (${p.currency_code})` : p.name}
              </option>
            ))}
          </select>
        </Field>
        {!tx.sender_account_sha256 && (
          <div className="sm:col-span-2">
            <Field label="Which of their accounts did this wire come from?">
              <select value={sourceAccountId}
                      onChange={(e) => setSourceAccountId(e.target.value)}
                      disabled={!userId}
                      className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
                <option value="">— select account —</option>
                {clientAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.account_type} ····{a.account_number_last4} · {a.holder_name}
                  </option>
                ))}
              </select>
            </Field>
            <p className="text-xs mt-1" style={{ color: colors.mutedSoft }}>
              {accountsError ?? (
                userId
                  ? 'The bank did not say who sent this transfer. Read it off the client’s transfer receipt — money is only ever credited to the person who sent it.'
                  : 'Choose the client first.'
              )}
            </p>
          </div>
        )}
        <div className="sm:col-span-2">
          <Field label="Match reason (visible in audit log)">
            <input value={matchReason} onChange={(e) => setMatchReason(e.target.value)}
                   className="w-full px-3 py-2 rounded-lg text-sm"
                   placeholder="e.g. Sender CLABE matches client's registered account"
                   style={inputStyle} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Internal notes (optional)">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      style={inputStyle} />
          </Field>
        </div>
      </div>

      <div className="flex justify-end mt-3">
        <button type="button" onClick={() => { submit(); }}
                disabled={
                  submitting || !userId || !productId || matchReason.length < 5
                  || (!tx.sender_account_sha256 && !sourceAccountId)
                }
                className="text-sm px-3 py-1.5 rounded-lg font-semibold"
                style={{ background: colors.gold, color: colors.navy }}>
          {submitLabel(submitting, existingRequestId !== '')}
        </button>
      </div>

      <p className="text-xs mt-2" style={{ color: colors.mutedSoft }}>
        {existingRequestId
          ? 'The wire will be matched to the selected pending deposit, which then becomes approvable.'
          : 'A pending deposit will be created.'}{' '}
        A <em>different</em> manager must then approve it from the deposits queue (SOD).
      </p>
    </div>
  );
}


function submitLabel(submitting: boolean, matchingExisting: boolean): string {
  if (submitting) return 'Attributing…';
  return matchingExisting ? 'Match wire to pending deposit' : 'Attribute & create pending deposit';
}

/** Shown when the pending-deposit lookup failed. The panel stays usable, but the
 *  person attributing must not assume there is nothing to match against. */
function AwaitingLookupWarning({ message }: Readonly<{ message: string | null }>): JSX.Element | null {
  if (!message) return null;
  return (
    <div className="sm:col-span-2 rounded-lg p-3 text-sm" role="alert"
         style={{ background: colors.warningBg, border: '1px solid #FCD34D', color: colors.ink }}>
      {message} Check the pending deposits for this client before creating a new one:
      the wire may belong to a request that already exists.
    </div>
  );
}

/** A pending request can receive this wire only when it is still awaiting one
 *  and its amount + currency equal the wire's — the same rule the backend
 *  enforces; filtering here just keeps impossible choices out of the picker. */
function isWireCandidate(d: DepositRequest, tx: BankTransaction): boolean {
  return (
    d.source === 'client_proposed'
    && d.currency_code === tx.currency_code
    && Number(d.amount) === Number(tx.amount)
  );
}


// ── Form helpers ──────────────────────────────────────────────────


interface FieldProps {
  label: string;
  children: React.ReactNode;
}

function Field({ label, children }: Readonly<FieldProps>): JSX.Element {
  return (
    <label className="block">
      <span className="block text-xs mb-1" style={{ color: colors.muted }}>
        {label}
      </span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  background: colors.canvas,
  color: colors.ink,
  border: `1px solid ${colors.border}`,
};
